import { useTheme, Headline, List, ActivityIndicator } from "react-native-paper";
import { useDispatch, useSelector } from "react-redux";
import { useFocusEffect } from "@react-navigation/native";
import {
  setPaymentStarted,
  setDefaultPaymentSourceUpdated,
} from "../../redux/actions/actionCreators";
import React, { useState, useEffect, useCallback, createRef } from "react";
import { View, StyleSheet, Platform, ScrollView, Dimensions } from "react-native";
import { Spacer } from "../../components/spacer/spacer";
import { RootState } from "../../redux/reducers";
import { Screen, Button, InfoBox, Text } from "../../components";
import { Stack } from "../../components/layout/stack";
import { useNavigation } from "@react-navigation/native";
import { isAvsReady } from "../../utils/vantiv";
import Big from "big.js";
import upperFirst from "lodash.upperfirst";
import toLower from "lodash.tolower";
import { startOfDay, add } from "date-fns";
import { HomeScreenProps } from "../../navigation/home-stack/home-stack";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { enterPaymentDetailsSchema } from "../home/schemas";
import { ThemeType } from "../../theme/theme";
import { SelectAPaymentMethod } from "../../components/select-payment-method/select-payment-method";
import { translate } from "../../i18n";
import { InfoBoxType, PaymentSourceType } from "../../enums";
import { DomusoApiService } from "../../api/DomusoApiService";
import moment from "moment-timezone";
import { addStartOfDayInformationWithTimezone } from "../../utils/date";
import { usePaymentMethodInformation } from "../../hooks/usePaymentMethodInformation";
import { AntDesign } from "@expo/vector-icons";
import { Inline } from "../../components/layout/inline";
import { testProps } from "../../utils/testProps";
import { BalanceDueExtension } from "../../components/header/balance-due-extension";
import { ProgressIndicator } from "ui-components";

const constants = require("domuso-constants");

const PaymentType = {
  ONE_SHOT: "ONE_SHOT",
};

// Needed to show Platform fees.
const DCashRecoveryPaymentFees = {
  rent_ach_minimum: 0,
  rent_cc_percent: 0,
};

const isIos = Platform.OS === "ios";
const windowWidth = Dimensions.get("window").width;
const windowWidthAdjusted = windowWidth > 804 ? 750 : windowWidth - 15;
const windowHeight = Dimensions.get("window").height;
const windowHeightAdjusted = windowHeight > 999 ? windowHeight : windowHeight * 0.75;

export const DCashRecoveryEnterPaymentDetails = ({ route }: HomeScreenProps) => {
  const navigation = useNavigation();
  const cognitoUser = useSelector((state: RootState) => state.auth.cognitoUser);
  const logger = useSelector((state: RootState) => state.logger);
  const theme = useTheme() as ThemeType;
  const dispatch = useDispatch();
  const { params } = route;
  const {
    balanceDue = 0,
    currentPropertyProfile,
    allowedPayments,
    propertyUnit,
    reservationId,
    blockedReservationId,
    hasRemainingDCashRecoveryPayments,
    remainingDCashRecoveryPaymentsAmount,
  } = params;

  params.currentPropertyFeeProfile = DCashRecoveryPaymentFees;

  let {
    certified_start_day,
    certified_end_day,
    allow_ach_payments,
    allow_card_payments,
  } = currentPropertyProfile || {};

  const [amount, setAmount] = useState(!balanceDue || balanceDue < 0 ? 0 : balanceDue / 100);

  // Acordin expansion states.
  const [isExpanded, setIsExpanded] = useState("1");
  const [isFirstQAExpanded, setIsFirstQAExpanded] = useState();
  const [isSecondQAExpanded, setIsSecondQAExpanded] = useState();

  const preSelectedPaymentSourceId = useSelector((state) => state.payment.paymentSourceId);
  let { paymentSources, firstValidSource } = usePaymentMethodInformation({
    certifiedDateRange: { certified_end_day, certified_start_day },
    allowedPayments,
    preSelectedPaymentSourceId,
    keepInactive: false,
    omitBA: false,
    allowACH: !!allow_ach_payments,
    allowCC: !!allow_card_payments,
  });

  const [selected, emitSelected] = useState<any>();
  const service = new DomusoApiService(cognitoUser, dispatch);
  const start = moment.utc();
  const [date, setDate] = useState(new Date(start));

  const setDefaultPaymentSource = () => {
    let paymentSource = firstValidSource;
    if (paymentSources && paymentSources.length && preSelectedPaymentSourceId) {
      paymentSource = paymentSources.find((source) => source.id === preSelectedPaymentSourceId);
    }
    if (paymentSource) {
      emitSelected({ ...paymentSource, paymentType: paymentSource.type });
    }
  };
  // this might need a clean-up
  if (
    (preSelectedPaymentSourceId && selected && selected.id !== preSelectedPaymentSourceId) ||
    (firstValidSource && !selected)
  ) {
    setDefaultPaymentSource();
  }

  // When navigating to the screen for some reason the default values of the first payment is shown
  // in case of multiple return payment.
  useEffect(() => {
    setValue("paymentAmount", !balanceDue || balanceDue < 0 ? 0 : balanceDue / 100);
  }, [balanceDue]);

  let defaultValues: any = {
    paymentAmount: amount,
    paymentDate: date,
  };

  const {
    register,
    handleSubmit,
    setValue,
    getValues,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(
      enterPaymentDetailsSchema({ allowPartialRentPayments: false, balanceDue: amount })
    ),
    defaultValues,
  });

  const layoutEffect = useCallback(() => {
    dispatch(setPaymentStarted(reservationId));
    setDefaultPaymentSource();
    // Comment out no need to check for CC property based block
    // service.checkUserCCPayBlock({ reservationId }).then((blockedRes) => {
    //   setHasUserCCPayBlock(!!blockedRes.blocked);
    // });
  }, []);
  useFocusEffect(layoutEffect);

  useEffect(() => {
    register("paymentAmount");
    register("paymentDate");
  }, [register]);

  const updateSelected = (value) => {
    dispatch(setDefaultPaymentSourceUpdated(value.id));
    emitSelected(value);
  };

  const getAmounts = () => {
    let paymentAmount = getValues("paymentAmount");
    if (!paymentAmount) return {};
    paymentAmount = Big(paymentAmount).mul(100);
    const amounts = {
      amount: paymentAmount,
      // These are determined later
      platformFee: 0,
      total: paymentAmount,
    };
    return amounts;
  };

  const onSubmit = async (data: any) => {
    const amounts = getAmounts();

    const nowTimestamp = moment.utc();
    const paymentDate = new Date(nowTimestamp);
    const pstDate = addStartOfDayInformationWithTimezone(paymentDate);

    let { last_four_digits, type, id, card_type, bank_account_type } = selected;

    const finalAmounts: any = {
      amount: Big(amounts.amount || 0),
    };
    if (type === PaymentSourceType.BANK_ACCOUNT) {
      finalAmounts.platformFee = DCashRecoveryPaymentFees.rent_ach_minimum;
      finalAmounts.total = finalAmounts.amount.add(finalAmounts.platformFee);
      card_type = bank_account_type;
    } else if (type === PaymentSourceType.CREDIT_CARD) {
      finalAmounts.platformFee = DCashRecoveryPaymentFees.rent_ach_minimum;
      finalAmounts.total = finalAmounts.amount.add(finalAmounts.platformFee);
    }

    if (type === PaymentSourceType.CREDIT_CARD && !isAvsReady(selected)) {
      return navigation.navigate("VerifyBillingAddress", {
        ...params,
        propertyUnit,
        reservationId,
        paymentDate,
        amounts: finalAmounts,
        paymentSourceId: id,
        paymentScheduleType: PaymentType.ONE_SHOT,
        last4Digits: last_four_digits,
        paymentSourceType: type,
        cardType: card_type,
        isCertified: false,
        fromSelectCertifiedPayment: false,
      });
    }
    return navigation.navigate("ReviewPayment", {
      ...params,
      propertyUnit,
      reservationId,
      paymentDate: pstDate,
      amounts: finalAmounts,
      paymentSourceId: id,
      paymentScheduleType: PaymentType.ONE_SHOT,
      last4Digits: last_four_digits,
      paymentSourceType: type,
      cardType: card_type,
      isCertified: true,
      isRecoveryPayment: true,
      blockedReservationId: blockedReservationId,
      hasRemainingDCashRecoveryPayments: hasRemainingDCashRecoveryPayments,
      remainingDCashRecoveryPaymentsAmount: remainingDCashRecoveryPaymentsAmount,
    });
  };

  const styles = getStyles();

  const getSelectedTitle = () => {
    if (!selected) return "Select Method of Payment";
    const isBankSelected = selected.paymentType === PaymentSourceType.BANK_ACCOUNT;
    return isBankSelected
      ? `${upperFirst(toLower(selected.bank_account_type))} **** **** **** ${
          selected.last_four_digits
        }`
      : `${selected.card_type} **** **** **** ${selected.last_four_digits}`;
  };

  const accordionTheme = { ...theme, colors: { ...theme.colors, text: "gray" } };

  return (
    <Screen
      style={styles.container}
      preset="scroll"
      backgroundColor={theme.colors.background}
      unsafe={true}
      dontUseAutoPadding={true}>
      <View style={styles.mainStack}>
        <ScrollView scrollEnabled={true} style={styles.scrollView}>
          <View>
            <View style={{ marginTop: 20 }}>
              <ProgressIndicator
                stepCount={2}
                currentPosition={0}
                labels={[
                  translate("homeStack.step1Payment") as string,
                  translate("homeStack.step2Payment") as string,
                ]}
                {...testProps("dcash-recovery-enter-payment-details@progress-indicator")}
              />
            </View>
            <View style={{ marginTop: 10 }}>
              <BalanceDueExtension balance={balanceDue} />
            </View>
          </View>
          <View style={styles.bodyContainer}>
            <Spacer size="medium"></Spacer>
            <Headline style={styles.headlineTitle}>
              {translate("dcashRecoveryStack.provideAlternatePaymentMethodTitle")}
            </Headline>
            <Stack space="tiny" style={styles.paymentMethodContainer}>
              <List.AccordionGroup
                expandedId={isFirstQAExpanded}
                onAccordionPress={() => {
                  if (isFirstQAExpanded) {
                    return setIsFirstQAExpanded(false);
                  }
                  return setIsFirstQAExpanded("1");
                }}
                {...testProps("dcash-recovery-enter-payment-details@dropdown-QA1")}>
                <List.Accordion
                  title={translate("dcashRecoveryStack.firstQATitle")}
                  id="1"
                  // Huge hack to get the icon to be a gray color when disabled
                  theme={accordionTheme}>
                  <Text style={styles.bodyText} tx="dcashRecoveryStack.firstQABody" />
                </List.Accordion>
              </List.AccordionGroup>
            </Stack>
            <Stack space="tiny" style={styles.paymentMethodContainer}>
              <List.AccordionGroup
                expandedId={isSecondQAExpanded}
                onAccordionPress={() => {
                  if (isSecondQAExpanded) {
                    return setIsSecondQAExpanded(false);
                  }
                  return setIsSecondQAExpanded("1");
                }}
                {...testProps("dcash-recovery-enter-payment-details@dropdown-QA2")}>
                <List.Accordion
                  title={translate("dcashRecoveryStack.secondQATitle")}
                  id="1"
                  // Huge hack to get the icon to be a gray color when disabled
                  theme={accordionTheme}>
                  <Text style={styles.bodyText} tx="dcashRecoveryStack.secondQABody" />
                </List.Accordion>
              </List.AccordionGroup>
            </Stack>
            <Spacer size="small"></Spacer>
            <Headline style={styles.headlineTitle}>
              {translate("dcashRecoveryStack.chooseMethodOfPayment")}
            </Headline>
            <Stack space="smaller">
              <Inline space="smaller">
                <AntDesign
                  style={styles.regularPaymentLogo}
                  name="exclamationcircleo"
                  size={theme.iconSize.smaller}
                />
                <Text style={styles.regularPaymentText} tx="homeStack.creditCardAndBankAccount" />
              </Inline>
              {Array.isArray(paymentSources) ? (
                <Stack space="tiny" style={styles.paymentMethodContainer}>
                  <List.AccordionGroup
                    expandedId={isExpanded}
                    onAccordionPress={() => {
                      if (isExpanded) {
                        return setIsExpanded(false);
                      }
                      return setIsExpanded("1");
                    }}>
                    <List.Accordion
                      title={getSelectedTitle()}
                      id="1"
                      // Huge hack to get the icon to be a gray color when disabled
                      theme={accordionTheme}>
                      <SelectAPaymentMethod
                        {...params}
                        propertyUnit={propertyUnit}
                        paymentSources={paymentSources}
                        shouldDisableCC={false}
                        amounts={getAmounts()}
                        selected={selected}
                        emitSelected={updateSelected}
                        reservationId={reservationId}
                      />
                    </List.Accordion>
                  </List.AccordionGroup>
                </Stack>
              ) : (
                <ActivityIndicator />
              )}
            </Stack>
            <Spacer size="small"></Spacer>
            <View style={styles.buttonContainer}>
              <Button
                tx="homeStack.continue"
                mode="contained"
                onPress={handleSubmit(onSubmit)}
                disabled={false}
                style={styles.submitButton}
                {...testProps("dcash-recovery-enter-payment-details@button-submit")}
              />
            </View>
          </View>
        </ScrollView>
      </View>
    </Screen>
  );
};

const getStyles = () => {
  const theme = useTheme() as ThemeType;
  const text = { color: theme.colors.surface };
  const { colors, spacing } = theme;
  return StyleSheet.create({
    container: {
      flex: 1,
      alignItems: "center",
      ...Platform.select({
        native: {
          backgroundColor: colors.background,
        },
        default: {
          backgroundColor: colors.lightGrey,
        },
      }),
    },
    scrollView: {
      flex: 1,
      width: "100%",
      height: windowHeightAdjusted,
      paddingBottom: 75,
      ...Platform.select({
        native: {
          paddingTop: 0,
          paddingLeft: 0,
          paddingRight: 0,
        },
        default: {
          paddingTop: 25,
          paddingLeft: 25,
          paddingRight: 25,
        },
      }),
    },
    mainStack: {
      display: "flex",
      flex: 1,
      maxWidth: 804,
      width: "100%",
      backgroundColor: theme.colors.background,
    },
    headlineTitle: {
      fontSize: theme.fontSize.large,
      fontWeight: "600",
    },
    regularPaymentText: {
      fontSize: theme.fontSize.small,
      color: theme.colors.text,
    },
    regularPaymentLogo: {
      color: theme.colors.text,
    },
    partialPaymentText: {
      color: colors.notification,
    },
    bodyText: {
      padding: 10,
      ...Platform.select({
        native: {
          maxWidth: windowWidthAdjusted,
        },
        default: {
          maxWidth: windowWidthAdjusted,
        },
      }),
    },
    paymentMethodContainer: {
      borderColor: colors.disabled,
      borderWidth: 1,
      marginBottom: 20,
    },
    buttonContainer: {
      ...Platform.select({
        default: {
          width: 325,
          marginLeft: "auto",
          marginRight: "auto",
        },
      }),
    },
    submitButton: {
      marginBottom: 30,
    },
    bodyContainer: {
      paddingRight: theme.spacing.medium,
      paddingLeft: theme.spacing.medium,
    },
  });
};
